import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { ConditionSetEditor } from '@/components/conditions/ConditionSetEditor';
import { USAGE_POINT_LABELS } from '@/components/conditions/labels';
import type { ConditionCatalog } from '@/components/conditions/types';

/** `/settings/conditions` (v0.4 章②、D-57)。
 *  利用箇所 (usage_point) ごとに条件セットの詳細モード (AND/OR) エディタを表示する。
 *  設計: docs/design/v04-shift-labor-management-detail.md §2-8 */
export function ConditionsSettingsPage() {
  const { data: catalog, isLoading } = useQuery<ConditionCatalog>({
    queryKey: ['condition-catalog'],
    queryFn: () => apiFetch<ConditionCatalog>('/condition-catalog'),
    staleTime: 5 * 60 * 1000,
  });
  const [activeUsagePointId, setActiveUsagePointId] = useState<string | null>(null);

  const usagePoints = useMemo(() => catalog?.usage_points ?? [], [catalog]);
  const attributeTypesById = useMemo(
    () => new Map((catalog?.attribute_types ?? []).map((t) => [t.attribute_type_id, t])),
    [catalog],
  );
  const effectiveActiveId = activeUsagePointId ?? usagePoints[0]?.usage_point_id ?? null;
  const activeUsagePoint = usagePoints.find((u) => u.usage_point_id === effectiveActiveId) ?? null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">条件集合</h2>
        <p className="text-sm text-text-sub mt-1">
          必要人員・可視性ルール・標準単価などの機能で使う「誰が対象か」の条件を、利用箇所ごとに登録します。
          ここで登録した条件は、対応する機能が実装され次第利用されます。
        </p>
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : usagePoints.length === 0 ? (
        <p className="text-sm text-text-sub">利用箇所が登録されていません。</p>
      ) : (
        <>
          <div className="border-b border-surface-border overflow-x-auto">
            <nav className="flex gap-1 min-w-max" aria-label="利用箇所">
              {usagePoints.map((up) => (
                <button
                  key={up.usage_point_id}
                  type="button"
                  onClick={() => setActiveUsagePointId(up.usage_point_id)}
                  aria-current={up.usage_point_id === effectiveActiveId}
                  className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 min-h-[44px] transition ${
                    up.usage_point_id === effectiveActiveId
                      ? 'border-brand-red text-brand-red font-semibold'
                      : 'border-transparent text-text-sub hover:text-text-body hover:border-surface-border'
                  }`}
                >
                  {USAGE_POINT_LABELS[up.usage_point_id] ?? up.usage_point_id}
                </button>
              ))}
            </nav>
          </div>
          {activeUsagePoint && (
            <ConditionSetEditor
              key={activeUsagePoint.usage_point_id}
              usagePointId={activeUsagePoint.usage_point_id}
              usagePoint={activeUsagePoint}
              attributeTypesById={attributeTypesById}
            />
          )}
        </>
      )}
    </div>
  );
}
